import { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import FilterPopover from '../../community/residents/components/FilterPopover.jsx';
import UserGroupEmptyState from './UserGroupEmptyState.jsx';
import NoneActionCell from '../../../components/v2/NoneActionCell.jsx';

/**
 * Column definitions for the user groups table.
 */
export const GROUP_COLUMN_DEFS = [
  { key: 'group_name', label: 'Group Name', sortable: true, filterable: true, required: true },
  { key: 'capcode', label: 'Pager Channel', sortable: true, filterable: true, required: false },
  { key: 'user_count', label: 'Users', sortable: true, filterable: false, required: true },
];

/**
 * UserGroupsTable — data table for user groups with sorting, filtering,
 * sticky header, row selection, and row actions.
 */
const UserGroupsTable = ({
  data,
  allData,
  visibleColumns,
  sortColumn,
  sortOrder,
  onSort,
  filters,
  onFilterChange,
  selectedIds,
  onSelectRow,
  onSelectAll,
  onEditGroup,
  onDeleteGroup,
  onAddGroup,
  onClearFilters,
  canManageGroups = true,
}) => {
  const activeColumns = useMemo(
    () => GROUP_COLUMN_DEFS.filter((col) => visibleColumns.includes(col.key)),
    [visibleColumns]
  );

  /** Collect unique filter values from the full (unfiltered) dataset */
  const getFilterValues = useCallback(
    (columnKey) => {
      const values = new Set();
      (allData || data).forEach((row) => {
        const val = row[columnKey];
        values.add(val != null && val !== '' ? String(val) : '(empty)');
      });
      return [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    },
    [allData, data]
  );

  const allSelected = data.length > 0 && data.every((r) => selectedIds.includes(r.group_id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const renderSortIcon = (columnKey) => {
    if (sortColumn !== columnKey) {
      return (
        <svg className="up-icon-sm up-sort-icon up-sort-icon--neutral" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
        </svg>
      );
    }
    return sortOrder === 'asc' ? (
      <svg className="up-icon-sm up-sort-icon up-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
      </svg>
    ) : (
      <svg className="up-icon-sm up-sort-icon up-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
      </svg>
    );
  };

  /** Renders a single cell value */
  const renderCell = (row, columnKey) => {
    switch (columnKey) {
      case 'group_name':
        return (
          <span className="up-cell-text" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
            {row.group_name}
          </span>
        );

      case 'capcode':
        return row.capcode ? (
          <span className="up-cell-text">{row.capcode}</span>
        ) : (
          <span className="up-cell-text--muted">—</span>
        );

      case 'user_count':
        // UsersPopoverCell owns both states; its "None" carries the same
        // click action as the badge.
        return <UsersPopoverCell users={row.users || []} onEdit={onEditGroup} row={row} />;

      default:
        return <span className="up-cell-text">{row[columnKey] || '—'}</span>;
    }
  };

  if (data.length === 0) {
    const hasAnyData = (allData || []).length > 0;
    return (
      <UserGroupEmptyState
        type={hasAnyData ? 'no-results' : 'no-data'}
        onAddGroup={hasAnyData || !canManageGroups ? undefined : onAddGroup}
        onClearFilters={hasAnyData ? onClearFilters : undefined}
      />
    );
  }

  return (
    <div className="up-table-wrap">
      <table className="up-table">
        <thead className="up-thead">
          <tr>
            {/* Checkbox column */}
            <th scope="col" className="up-th up-th--checkbox">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => { if (el) el.indeterminate = someSelected; }}
                onChange={onSelectAll}
                className="up-checkbox"
                aria-label="Select all groups"
              />
            </th>

            {/* Actions column */}
            <th scope="col" className="up-th up-th--actions">Edit</th>

            {/* Data columns */}
            {activeColumns.map((col) => (
              <th key={col.key} scope="col" className="up-th">
                <div className="up-th__content">
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(col.key)}
                      className="up-th__sort-btn"
                      aria-label={`Sort by ${col.label}`}
                    >
                      {col.label}
                      {renderSortIcon(col.key)}
                    </button>
                  ) : (
                    <span>{col.label}</span>
                  )}

                  {col.filterable ? (
                    <FilterPopover
                      columnKey={col.key}
                      label={col.label}
                      values={getFilterValues(col.key)}
                      selectedValues={filters[col.key] || []}
                      onChange={onFilterChange}
                    />
                  ) : null}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="up-tbody">
          {data.map((row) => {
            const isSelected = selectedIds.includes(row.group_id);
            return (
              <tr
                key={row.group_id}
                className={`up-tr ${isSelected ? 'is-selected' : ''}`}
              >
                {/* Checkbox */}
                <td className="up-td up-td--checkbox">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onSelectRow(row.group_id)}
                    className="up-checkbox"
                    aria-label={`Select ${row.group_name}`}
                  />
                </td>

                {/* Row actions kebab */}
                <td className="up-td up-td--actions">
                  <GroupRowActions
                    group={row}
                    onEdit={onEditGroup}
                    onDelete={onDeleteGroup}
                    canDelete={canManageGroups}
                  />
                </td>

                {/* Data cells */}
                {activeColumns.map((col) => (
                  <td key={col.key} className="up-td">
                    {renderCell(row, col.key)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/**
 * UsersPopoverCell — inline popover showing user names and roles in a group.
 */
const UsersPopoverCell = ({ users, onEdit, row }) => {
  // When onEdit is provided, badge click opens the edit-group drawer
  // (convenience shortcut). Hover preview stays in both modes.
  const isClickToEdit = typeof onEdit === 'function';
  const [open, setOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState({});
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

    const computePosition = () => {
        if (ref.current) {
            const rect = ref.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            if (spaceBelow < 200) {
                setPopoverStyle({ position: 'fixed', bottom: `${window.innerHeight - rect.top + 4}px`, left: `${rect.left}px`, top: 'auto', right: 'auto', margin: 0, zIndex: 9999, transform: 'none' });
            } else {
                setPopoverStyle({ position: 'fixed', top: `${rect.bottom + 4}px`, left: `${rect.left}px`, bottom: 'auto', right: 'auto', margin: 0, zIndex: 9999, transform: 'none' });
            }
        }
    };

    const handleOpen = (nextOpen) => {
        if (nextOpen) computePosition();
        setOpen(nextOpen);
    };

  const handleBadgeClick = () => {
    if (isClickToEdit) {
      setOpen(false);
      onEdit(row);
    } else {
      handleOpen(!open);
    }
  };

  // Empty groups keep the muted "None" look but carry the badge's action.
  if (!users || users.length === 0) {
    return (
      <NoneActionCell
        className="up-cell-text--muted"
        onClick={isClickToEdit ? () => onEdit(row) : undefined}
        title="Click to edit group"
      />
    );
  }

  return (
    <div className="up-users-cell" ref={ref}>
      <button
        type="button"
        className="up-users-badge"
        onClick={handleBadgeClick}
        onMouseEnter={() => handleOpen(true)}
        onMouseLeave={() => handleOpen(false)}
        aria-expanded={isClickToEdit ? undefined : open}
        aria-label={isClickToEdit
          ? `Edit group — ${users.length} user${users.length !== 1 ? 's' : ''}`
          : `${users.length} user${users.length !== 1 ? 's' : ''} in group`}
        title={isClickToEdit ? 'Click to edit group' : undefined}
      >
        <i className="fe fe-users" style={{ fontSize: '12px' }} aria-hidden="true" />
        {users.length}
      </button>
      {open ? (
        <div className="up-users-popover" style={popoverStyle}>
          <div className="up-users-popover__title">Users ({users.length})</div>
          <ul className="up-users-popover__list">
            {users.map((u, i) => (
              <li key={u.user_id || i} className="up-users-popover__item">
                <span className="up-users-popover__name">{u.display_name}</span>
                {u.user_role_name ? (
                  <span className="up-users-popover__role">{u.user_role_name}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

/**
 * GroupRowActions — kebab menu for per-row actions.
 */
const GroupRowActions = ({ group, onEdit, onDelete, canDelete = true }) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

    const handleToggle = () => {
        if (!open && menuRef.current) {
            const rect = menuRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            if (spaceBelow < 180) {
                setMenuStyle({ position: 'fixed', bottom: `${window.innerHeight - rect.top + 4}px`, right: `${window.innerWidth - rect.right}px`, top: 'auto', left: 'auto', margin: 0, zIndex: 9999 });
            } else {
                setMenuStyle({ position: 'fixed', top: `${rect.bottom + 4}px`, right: `${window.innerWidth - rect.right}px`, bottom: 'auto', left: 'auto', margin: 0, zIndex: 9999 });
            }
        }
        setOpen(!open);
    };

  return (
    <div className="up-row-actions" ref={menuRef}>
      <button
        type="button"
        onClick={handleToggle}
        className="up-row-actions__trigger"
        aria-label={`Actions for ${group.group_name}`}
        aria-expanded={open}
      >
        <i className="fe fe-more-vertical" aria-hidden="true" />
      </button>

      {open ? (
        <div className="up-row-actions__menu" style={menuStyle}>
          <button
            type="button"
            onClick={() => { onEdit(group); setOpen(false); }}
            className="up-row-actions__item"
          >
            <svg className="up-icon-md up-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
            </svg>
            Edit Group
          </button>
          {canDelete ? (
            <button
              type="button"
              onClick={() => { onDelete(group); setOpen(false); }}
              className="up-row-actions__item up-row-actions__item--danger"
            >
              <svg className="up-icon-md up-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
              Delete Group
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default UserGroupsTable;
